import { getAccessToken } from "../auth";
import type { RowSlaPosture } from "../utils/ticketStatus";

const base = import.meta.env.VITE_API_BASE;

// Fail loudly at module load if the API base wasn't baked into the build.
// Without this, `base` is undefined and every fetch silently resolves against
// a relative URL — a confusing, hard-to-diagnose failure mode in deployed
// builds where the env var was missed.
if (!base) {
  throw new Error(
    "VITE_API_BASE is not configured. Set it in the build environment (.env / CI).",
  );
}

// Hard ceiling on any single request so an unresponsive backend can't hang
// the UI indefinitely. AbortController fires after this many ms.
const REQUEST_TIMEOUT_MS = 30_000;

// "View as user" (impersonation): when an iQuest staff member starts a view-as
// session, the target user id is stashed here and every request then carries it
// as the X-Impersonate-User header, so the backend runs the request as that
// client user (strictly read-only). sessionStorage so it dies with the tab and
// never leaks to another tab. The getter is read on every request; the setter/
// clearer are driven by services/impersonation.ts around start/stop.
const IMPERSONATE_KEY = "iq_impersonate_user_id";

export function getImpersonationUserId(): string | null {
  try {
    return sessionStorage.getItem(IMPERSONATE_KEY);
  } catch {
    return null;
  }
}

export function setImpersonationUserId(userId: string): void {
  try {
    sessionStorage.setItem(IMPERSONATE_KEY, userId);
  } catch {
    /* sessionStorage unavailable (private mode / disabled): no-op */
  }
}

export function clearImpersonationUserId(): void {
  try {
    sessionStorage.removeItem(IMPERSONATE_KEY);
  } catch {
    /* no-op */
  }
}

/** Bearer + view-as headers for a request.
 *
 * EVERY authenticated call must go through this, including the raw-fetch
 * endpoints below that can't route through req() (binary downloads and
 * multipart uploads). Omitting the view-as header does not fail safe: the
 * request authenticates as the real staff member while the UI believes it is
 * running as the client, so the monthly report 400'd with "company_id is
 * required for iQuest staff" mid-preview, and the upload paths wrote as the
 * staff actor instead of being refused by the read-only impersonation guard.
 */
async function authHeaders(extra?: HeadersInit): Promise<Headers> {
  const token = await getAccessToken();
  const headers = new Headers(extra);
  headers.set("Authorization", `Bearer ${token}`);
  // Carry the active view-as target, if any, so the backend swaps the effective
  // user for this request. Absent for normal (non-impersonating) sessions.
  const impersonateId = getImpersonationUserId();
  if (impersonateId) headers.set("X-Impersonate-User", impersonateId);
  return headers;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  // A caller signal (the summary preview cancels in-flight renders) aborts
  // the same fetch. The timeout message stays for a timeout with no caller abort.
  const caller = init.signal;
  const onCallerAbort = () => controller.abort();
  if (caller) {
    if (caller.aborted) controller.abort();
    else caller.addEventListener("abort", onCallerAbort);
  }
  let r: Response;
  try {
    r = await fetch(`${base}${path}`, { ...init, headers, signal: controller.signal });
  } catch (e) {
    if (caller?.aborted) throw e;
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s: ${path}`);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
    caller?.removeEventListener("abort", onCallerAbort);
  }
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`${r.status} ${r.statusText}: ${text}`);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

/** Pull a filename out of a Content-Disposition header, preferring the RFC 5987
 * ``filename*=UTF-8''...`` form (percent-decoded) over the legacy ``filename=``.
 * Returns null when neither is present. */
function parseContentDispositionFilename(disposition: string): string | null {
  const ext = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (ext?.[1]) {
    try {
      return decodeURIComponent(ext[1].trim());
    } catch {
      /* fall through to the legacy form */
    }
  }
  const basic = /filename="?([^";]+)"?/i.exec(disposition);
  return basic?.[1]?.trim() ?? null;
}

export interface Me {
  id: string;
  email: string;
  display_name: string;
  role: string;
  company_id: string;
  permissions: string[];
  company_name?: string | null;
  logo_url?: string | null;
  accent_color?: string | null;
  /**
   * Server-side delegation kill-switch (config.approval_delegation_enabled).
   * When false the engine routes approvals to the originally named approver and
   * the UI hides the Delegation page + admin tab. Absent on older API builds,
   * so treat `undefined` as enabled (default true).
   */
  approval_delegation_enabled?: boolean;
  /**
   * Company-level file-attach capability (effective ticket-attach gate). When
   * false, the UI hides every upload affordance — the new-ticket evidence
   * dropzone and form `file_upload` fields. Absent on older API builds, so
   * treat `undefined` as enabled (default true); the server still enforces it.
   */
  can_attach?: boolean;
  /**
   * "View as user": true when an iQuest staff member is previewing the portal
   * as this (impersonated) user. The whole Me payload reflects the impersonated
   * user; `impersonator` names the real staff actor so the UI can render the
   * read-only banner. Absent on older API builds (treat undefined as false).
   */
  impersonating?: boolean;
  impersonator?: { display_name: string; email: string } | null;
  /**
   * Reporting engine (BrightGauge replacement). `reporting_enabled` echoes the
   * server kill-switch; `may_view_reports` is the caller's view access (staff /
   * client_admin / the view_reports grant). Absent on older API builds (treat
   * undefined as false). The build-time VITE_FEATURE_REPORTING flag gates the
   * nav/route on top of these.
   */
  reporting_enabled?: boolean;
  may_view_reports?: boolean;
  /**
   * Status-alert "Send test to me". Echoes the server flag. Absent on older
   * API builds (treat undefined as false). The server still refuses the
   * endpoint when the flag is off.
   */
  status_alert_test_send_enabled?: boolean;
}

/**
 * The single GLOBAL report-dashboard widget layout (replaces the old per-user
 * localStorage layout). `widgets` is the opaque Widget[] the reporting dashboard
 * renders; `editable` is true only for iQuest staff (the server enforces it).
 * `version` drives optimistic-concurrency on save.
 */
export interface ReportingDashboardResponse {
  widgets: unknown[];
  version: number;
  updated_at?: string | null;
  editable: boolean;
}

/**
 * Tombstone state returned by the account-deletion endpoint. The account is
 * marked for deletion (anonymisation) rather than removed inline, so the
 * response names the state and effective timestamp explicitly.
 */
export interface AccountDeletionTombstone {
  status: string;
  deletion_requested_at: string | null;
  effective_at: string | null;
}

export type TicketScope = "my" | "company" | "all";

export interface AutotaskTicketListItem {
  id: number | null;
  ticketNumber: string | null;
  title: string | null;
  status_label: string | null;
  priority_label: string | null;
  age_hours: number | null;
  assignee_name: string | null;
  requester_name: string | null;
  company_name: string | null;
  created_at: string | null;
  first_response_due: string | null;
  first_response_at: string | null;
  resolved_due: string | null;
  csat_temperature: string | null;
  /** Where this ticket stands on its first response, computed server-side from
   *  the one shared rule (services/sla_posture.py). Was worked out in the
   *  browser from a flat four wall-clock hours, which disagreed with the
   *  Response times page (issue #102). */
  sla_posture: RowSlaPosture | null;
}

export interface AutotaskTicketListResponse {
  items: AutotaskTicketListItem[];
  count: number;
  page?: number;
  page_size?: number;
  scope: TicketScope;
  /** Set when served from the cache: when the mirror last synced. */
  synced_at?: string | null;
}

export interface AutotaskTicketNote {
  id: number | null;
  title: string | null;
  description: string | null;
  publish: number | null;
  created_at: string | null;
  created_by: string | null;
}

/** A technician's logged work entry on a ticket. The API exposes only the
 * client-safe fields (summary/hours/date/technician) — never internal notes,
 * billing codes or rates. */
export interface AutotaskTicketTimeEntry {
  id: number | null;
  summary: string | null;
  hours: number | null;
  date_worked: string | null;
  resource_name: string | null;
}

export interface AutotaskTicketSla {
  first_response_due: string | null;
  first_response_at: string | null;
  resolved_due: string | null;
  resolved_at: string | null;
  service_level_agreement_id: number | null;
  service_level_agreement_has_been_met: boolean | null;
}

export interface TicketFollower {
  resource_id: number;
  name: string | null;
  role_label: string | null;
}

/** A file attached to a ticket. Metadata only — the bytes are streamed on
 * demand through the gated download endpoint, never inlined here. */
export interface AutotaskTicketAttachment {
  id: number | null;
  title: string | null;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  created_at: string | null;
  created_by: string | null;
}

/** Server-computed write capabilities for the caller on this ticket. The UI
 * renders the reply box / attach control / reopen-resolve buttons purely from
 * this block; every write endpoint re-checks the same gates server-side. */
export interface TicketCapabilities {
  can_add_note: boolean;
  can_attach: boolean;
  can_change_status: boolean;
  allowed_status_actions: Array<"reopen" | "resolve" | "close">;
}

export interface AutotaskTicketDetail {
  id: number | null;
  ticketNumber: string | null;
  title: string | null;
  description: string | null;
  resolution: string | null;
  status_label: string | null;
  status_id: number | null;
  priority_label: string | null;
  priority_id: number | null;
  category_label: string | null;
  company_name: string | null;
  company_id: number | null;
  assignee_name: string | null;
  assignee_id: number | null;
  requester_contact_id: number | null;
  /** Who raised the ticket (the Autotask contact), resolved to a name. Detail
   *  used to carry only the raw contact id, so the pane couldn't name them. */
  requester_name: string | null;
  created_at: string | null;
  last_activity_at: string | null;
  age_hours: number | null;
  is_closed: boolean;
  /** In the Resolved holding status: closed-ish but still inside the customer
   * reopen window. Distinguishes a reopenable Resolved ticket from a hard-closed
   * (Complete/Cancelled) one that cannot be reopened. */
  is_resolved: boolean;
  sla: AutotaskTicketSla;
  csat_temperature: string | null;
  csat_comment: string | null;
  /**
   * True only when the signed-in user is this ticket's Autotask contact.
   * Staff and View-as sessions are always false. Absent on older responses;
   * anything other than true means this viewer may not rate.
   */
  viewer_is_requester?: boolean;
  /**
   * Ticket-contact identity for the Customer Thermometer rating link.
   * Returned only when `viewer_is_requester` is true and the contact has an
   * email. These are the contact's own values, never the /api/me profile.
   */
  csat_contact_email?: string;
  csat_contact_first_name?: string;
  csat_contact_last_name?: string;
  notes: AutotaskTicketNote[];
  followers: TicketFollower[];
  time_entries: AutotaskTicketTimeEntry[];
  attachments: AutotaskTicketAttachment[];
  /** Present on detail responses; absent capabilities default to all-off. */
  capabilities?: TicketCapabilities;
  /** Set when served from the cache: when this ticket last synced. */
  synced_at?: string | null;
}

/** Columns the ticket list can be ordered by. No "sla": that pill is computed
 *  per request from the due dates and the AU holiday calendar, so there is no
 *  stored column for the server to order on. */
export type TicketSortKey =
  | "title"
  | "requester"
  | "company"
  | "priority"
  | "status"
  | "assignee"
  | "age";

export type TicketSortDir = "asc" | "desc";

export interface TicketListParams {
  scope?: TicketScope;
  status?: string;
  priority?: string;
  q?: string;
  page?: number;
  page_size?: number;
  /** Sorting is server-side: it orders the whole result set before paging, so
   *  it reaches tickets the browser has not loaded yet. */
  sort?: TicketSortKey;
  dir?: TicketSortDir;
}

export type FeedbackCategory = "bug" | "feature" | "question" | "other";

export interface FeedbackCreate {
  category: FeedbackCategory;
  subject: string;
  description: string;
  diagnostics: Record<string, unknown>;
  related_autotask_ticket_id?: string;
  related_form_request_id?: string;
  related_kb_article_id?: string;
  page_url?: string;
  attachment_urls?: string[];
}

export interface FeedbackAttachment {
  url: string;
  blob_name: string;
}

export interface SupportTicket {
  id: string;
  category: FeedbackCategory;
  subject: string;
  status: "open" | "closed";
  created_at: string;
  updated_at: string;
  github_issue_url: string | null;
  has_unread_support_reply: boolean;
}

export interface TicketComment {
  id: string;
  content: string;
  author_name: string | null;
  source: "user" | "support";
  created_at: string;
}

export interface SupportTicketDetail extends SupportTicket {
  description: string;
  related_autotask_ticket_id?: string | null;
  related_form_request_id?: string | null;
  related_kb_article_id?: string | null;
  page_url?: string | null;
  comments: TicketComment[];
}

export type AnnouncementSeverity = "incident" | "maintenance" | "info";

// How a status-alert summary is stored. Existing alerts stay "plain".
export type AnnouncementBodyFormat = "plain" | "markdown";

// Status Alerts (Implementation Spec v1.2). An alert_type turns a plain
// announcement into a lifecycle-aware Status Alert; null => plain announcement.
export type AlertType = "outage" | "maintenance" | "enhancement";

export interface ServiceItem {
  name: string;
  description: string;
  status: string;
}

export interface AnnouncementPublic {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  dismissible: boolean;
  expires_at: string | null;
  created_at: string;
  // Status Alert scheduling, surfaced so the banner can state the window.
  // Null for legacy plain announcements and for outages (no window).
  alert_type: AlertType | null;
  window_start: string | null;
  window_end: string | null;
  // Rendered on read. `body` stays the stored source.
  // Null when rendering failed: the banner shows `body` as plain text.
  body_format: AnnouncementBodyFormat;
  body_html: string | null;
  body_excerpt_html: string | null;
  body_truncated: boolean;
  // Banner version. Absent on an older API; the banner then uses created_at.
  updated_at?: string;
  lifecycle_state?: string | null;
}

export interface AnnouncementAdmin extends Omit<
  AnnouncementPublic,
  "body_html" | "body_excerpt_html" | "body_truncated"
> {
  target_all: boolean;
  target_company_ids: string[] | null;
  is_active: boolean;
  created_by_user_id: string;
  created_by_name: string | null;
  updated_at: string;
  // Status Alert lifecycle (null for legacy plain announcements).
  alert_type: AlertType | null;
  reference: string | null;
  lifecycle_state: string | null;
  affected_site: string | null;
  services: ServiceItem[] | null;
  window_start: string | null;
  window_end: string | null;
  send_at: string | null;
  reminder_lead: string | null;
  reminder_at: string | null;
  reminder_sent_at: string | null;
  // Per-alert email switch (false = "banner only", no customer email).
  email_enabled: boolean;
  update_count: number;
  last_update_at: string | null;
}

export interface AnnouncementCreate {
  title: string;
  body: string;
  // Omitted saves stay plain. The status-alert composer sends "markdown".
  body_format?: AnnouncementBodyFormat;
  severity?: AnnouncementSeverity | null;
  target_all: boolean;
  target_company_ids?: string[] | null;
  expires_at?: string | null;
  alert_type?: AlertType | null;
  reference?: string | null;
  lifecycle_state?: string | null;
  affected_site?: string | null;
  services?: ServiceItem[] | null;
  window_start?: string | null;
  window_end?: string | null;
  send_at?: string | null;
  reminder_lead?: string | null;
  email_enabled?: boolean;
  is_draft?: boolean;
}

/** Composer draft sent to the internal test-send endpoint. No recipient field. */
export interface StatusAlertTestSendRequest {
  title: string;
  body: string;
  body_format?: AnnouncementBodyFormat;
  alert_type: AlertType;
  lifecycle_state: string;
  reference?: string | null;
  affected_site?: string | null;
  services?: ServiceItem[] | null;
  window_start?: string | null;
  window_end?: string | null;
  target_all: boolean;
  target_company_ids?: string[] | null;
}

export interface StatusAlertTestSendResponse {
  sent_to: string;
  rendered_for_company_id: string;
  rendered_for_company_name: string;
  subject: string;
}

export interface AnnouncementUpdate {
  title?: string;
  body?: string;
  body_format?: AnnouncementBodyFormat;
  severity?: AnnouncementSeverity;
  target_all?: boolean;
  target_company_ids?: string[] | null;
  is_active?: boolean;
  expires_at?: string | null;
  alert_type?: AlertType | null;
  reference?: string | null;
  lifecycle_state?: string | null;
  affected_site?: string | null;
  services?: ServiceItem[] | null;
  window_start?: string | null;
  window_end?: string | null;
  send_at?: string | null;
  reminder_lead?: string | null;
  email_enabled?: boolean;
}

export interface AlertUpdate {
  id: string;
  state: string;
  note: string | null;
  posted_by_user_id: string;
  posted_by_name: string | null;
  email_sent_count: number;
  created_at: string;
}

export interface PostUpdateRequest {
  state: string;
  note?: string | null;
}

export interface ResolveRecipientsRequest {
  alert_type?: AlertType | null;
  target_all: boolean;
  target_company_ids?: string[] | null;
}

export interface RecipientFunnel {
  target_all: boolean;
  targeted_company_count: number;
  company_names: string[];
  roster: number;
  never_signed_in: number;
  portal_accounts: number;
  email_off_count: number;
  email_off_company_names: string[];
  email_off_accounts: number;
  is_incident: boolean;
  muted: number;
  email_channel_off: number;
  suppressed: number;
  will_email: number;
  personalised_copies: number;
  // Non-portal Entra mailbox audience (issue #32). Read from the cipp_mailbox
  // mirror filled by the daily 05:00 AWST sweep, so these are always real
  // counts; cipp_lookup_ok is false only when the mirror read itself failed.
  cipp_enabled: boolean;
  cipp_lookup_ok: boolean;
  cipp_mailboxes: number;
  cipp_domain_blocked: number;
  cipp_suppressed: number;
  cipp_will_email: number;
  // When the oldest targeted tenant last synced (ISO), plus the reasons a count
  // may be lower than expected. These exist so a zero is explained rather than
  // presented as "nobody to email": a client with no M365 tenant connected and a
  // client whose tenant genuinely has no non-portal mailboxes used to render
  // identically.
  cipp_as_of: string | null;
  cipp_unconnected_companies: number;
  cipp_never_synced_companies: number;
  cipp_stale_companies: number;
  cipp_failed_companies: number;
}

export interface CippMailboxRefreshResult {
  companies: number;
  synced: number;
  failed: number;
  mailboxes: number;
}

export interface StatusAlertClient {
  id: string;
  name: string;
  notifications_enabled: boolean;
  portal_accounts: number;
}

export interface RenderSummaryRequest {
  body: string;
  body_format: AnnouncementBodyFormat;
  alert_type?: AlertType | null;
  has_window?: boolean | null;
}

export interface RenderedSummary {
  email_html: string;
  portal_html: string;
  excerpt_html: string;
  text: string;
  truncated: boolean;
  warnings: string[];
}

export interface AdminAnnouncementsPage {
  items: AnnouncementAdmin[];
  total: number;
  page: number;
  page_size: number;
}

// ---- Admin: Users + permissions --------------------------------------------

export interface AdminUserRow {
  id: string;
  email: string;
  display_name: string;
  role: string;
  status: string;
  company_id: string;
  company_name: string;
  permissions: string[];
  last_login_at: string | null;
  created_at: string;
  /**
   * Work email can be corrected only before any sign-in binding exists.
   * Absent on an older API, which the person drawer treats as locked.
   */
  email_editable?: boolean;
}

export interface AdminUsersPage {
  items: AdminUserRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface AdminCompanyRow {
  id: string;
  name: string;
}

/** A client user selectable as a "view as" (impersonation) target. */
export interface ImpersonationTarget {
  id: string;
  display_name: string;
  email: string;
  role: string;
}

/** Confirmation returned when a "view as" session starts. */
export interface ImpersonationTargetSummary {
  id: string;
  display_name: string;
  email: string;
  company_id: string;
  company_name: string | null;
  role: string;
}

export type SsoTrustMode = "b2b_guest" | "federated";

/** Per-section onboarding state for one customer. `ok` finished, `warn`
 *  started but broken, `todo` untouched. Only present when the caller asked
 *  for `include: "health"`. */
export interface AdminClientSetupHealth {
  overview: "ok" | "warn" | "todo";
  users: "ok" | "warn" | "todo";
  sso: "ok" | "warn" | "todo";
  forms: "ok" | "warn" | "todo";
  sync: "ok" | "warn" | "todo";
  /** `null` when the reporting engine is off globally, in which case it is not
   *  part of this customer's setup and is left out of done/total. */
  reporting: "ok" | "warn" | "todo" | null;
  done: number;
  total: number;
  /** Section keys whose source data could not be read. The matching state is a
   *  placeholder and must not be shown as onboarding status. */
  unavailable?: string[];
  /** The ticket cache's refusal text when it will not sync this customer (their
   *  Autotask id is shared by another active customer). `sync` reads "warn". */
  sync_refused?: string | null;
}

export interface AdminClientListItem {
  id: string;
  name: string;
  slug: string;
  autotask_company_id: number | null;
  m365_tenant_id: string | null;
  sso_trust_mode: SsoTrustMode;
  is_active: boolean;
  notifications_enabled: boolean;
  user_count: number;
  created_at: string;
  /** Null unless the request passed `include: "health"`. */
  setup_health?: AdminClientSetupHealth | null;
  tickets_30d?: number | null;
  /** Set when health was requested and some or all of this customer's checks
   *  could not be calculated. The customer row itself is still present. */
  health_error?: string | null;
  /** The same messages one by one (an older API omits it). */
  health_errors?: string[];
}

export interface AdminClientDetail extends AdminClientListItem {
  logo_url: string | null;
  accent_color: string | null;
  welcome_message: string | null;
  // Customer contact details (copied from Autotask once, then portal-maintained).
  phone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  web_address: string | null;
  // SSO / authentication policy. Non-null in the response: the backend columns
  // are NOT NULL with server defaults after migration 0017.
  allowed_email_domains: string[];
  require_mfa: boolean;
  auto_provision_users: boolean;
  sso_required: boolean;
  session_timeout_minutes: number;
  updated_at: string;
  /** Null unless the request passed `include: "health"`. */
  setup_health?: AdminClientSetupHealth | null;
  tickets_30d?: number | null;
}

export interface AdminClientCreate {
  name: string;
  autotask_company_id?: number | null;
  m365_tenant_id?: string | null;
  sso_trust_mode?: SsoTrustMode;
  notifications_enabled?: boolean;
  logo_url?: string | null;
  accent_color?: string | null;
  welcome_message?: string | null;
  // Customer contact details, optionally seeded from the linked Autotask company.
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  web_address?: string | null;
}

export interface AdminClientUpdate {
  name?: string;
  autotask_company_id?: number | null;
  m365_tenant_id?: string | null;
  sso_trust_mode?: SsoTrustMode;
  is_active?: boolean;
  notifications_enabled?: boolean;
  logo_url?: string | null;
  accent_color?: string | null;
  welcome_message?: string | null;
  // Customer contact details (omitted = unchanged).
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  web_address?: string | null;
  // SSO / authentication policy. All optional (omitted = unchanged) so the
  // backend exclude_unset diff still works. Send [] (not null) to clear
  // allowed_email_domains.
  allowed_email_domains?: string[] | null;
  require_mfa?: boolean | null;
  auto_provision_users?: boolean | null;
  sso_required?: boolean | null;
  session_timeout_minutes?: number | null;
}

// ---- Admin: permanent customer removal --------------------------------------

export interface AdminClientDeletionRow {
  key: string;
  label: string;
  count: number;
}

/**
 * Pre-flight for a permanent delete. `rows` is the manifest the purge would
 * destroy, counted with the same predicates the delete runs. `blockers` lists
 * every reason the delete is currently refused (portal still enabled, own
 * tenant, shared dependencies) — `can_delete` is just "no blockers".
 */
export interface AdminClientDeletionImpact {
  company_id: string;
  name: string;
  slug: string;
  is_active: boolean;
  autotask_company_id: number | null;
  user_count: number;
  rows: AdminClientDeletionRow[];
  total_rows: number;
  blockers: string[];
  can_delete: boolean;
  // The exact string the admin has to type to confirm.
  confirm_phrase: string;
}

export interface AdminClientDeleteResult {
  company_id: string;
  name: string;
  slug: string;
  total_rows_deleted: number;
  rows_deleted: Record<string, number>;
}

// ---- Admin: Autotask company lookup (Create from Autotask) ------------------

export interface AutotaskCompanySearchItem {
  autotask_id: number;
  name: string;
  is_active: boolean;
  synced_at: string | null;
}

export interface AutotaskCompanyContactPreview {
  id: number;
  full_name: string | null;
  email: string | null;
}

export interface AutotaskCompanyLink {
  id: string;
  name: string;
}

export interface AutotaskCompanyDetail {
  autotask_id: number;
  name: string;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  web_address: string | null;
  is_active: boolean | null;
  contact_count: number;
  contacts: AutotaskCompanyContactPreview[];
  // Set when a portal customer already links this Autotask id.
  already_linked: AutotaskCompanyLink | null;
}

export interface AutotaskSyncResult {
  resources: number;
  companies: number;
  contacts: number;
  picklists: number;
  synced_at: string;
}

// ---- Admin: import portal users from synced Autotask contacts --------------

export interface AutotaskContactCandidate {
  autotask_contact_id: number;
  full_name: string | null;
  email: string | null;
  // A portal user with this email already exists -> would be skipped.
  already_user: boolean;
  // Has an email and so can become a login account.
  importable: boolean;
}

export interface AutotaskContactImportPreview {
  company_id: string;
  // null when the portal company is not linked to an Autotask company.
  autotask_company_id: number | null;
  contacts: AutotaskContactCandidate[];
}

export interface AutotaskContactImportResult {
  created: number;
  skipped_existing: number;
  skipped_no_email: number;
  created_users: AdminUserRow[];
}

// ---- Admin: per-company ticket-sync config ---------------------------------

/** One badge token from the design system, used to colour a mapped status. */
export interface StatusDisplayEntry {
  label: string;
  color: string;
}

/** A queue / priority / status option mirrored from Autotask. */
export interface AdminPicklistOption {
  value: number;
  label: string;
}

export interface AdminTicketSyncConfig {
  company_id: string;
  read_enabled: boolean;
  write_enabled: boolean;
  default_queue_id: number | null;
  default_priority_id: number | null;
  status_display_mapping: Record<string, StatusDisplayEntry>;
  sync_notes: boolean;
  client_can_add_notes: boolean;
  updated_at: string | null;
  // Advisory / computed flags from the backend (read-only).
  write_globally_enabled: boolean;
  write_effective: boolean;
  note_create_supported: boolean;
  note_create_effective: boolean;
  // Same as write_effective. Status changes follow ticket create.
  status_change_effective: boolean;
  available_queues: AdminPicklistOption[];
  available_priorities: AdminPicklistOption[];
  available_statuses: AdminPicklistOption[];
  forced_off: string[];
}

export interface AdminTicketSyncConfigUpdate {
  read_enabled?: boolean;
  write_enabled?: boolean;
  default_queue_id?: number | null;
  default_priority_id?: number | null;
  status_display_mapping?: Record<string, StatusDisplayEntry>;
  sync_notes?: boolean;
  client_can_add_notes?: boolean;
  reason?: string | null;
}

/**
 * Field-global ticket-sync options (`GET /api/admin/ticket-sync-options`).
 *
 * The new-customer wizard configures ticket sync before the company exists, so
 * it cannot use the per-company GET. `suggested_*` are the instance-wide
 * fallbacks the Autotask create path already uses, pre-filtered to ids the PUT
 * will accept.
 */
export interface AdminTicketSyncOptions {
  available_queues: AdminPicklistOption[];
  available_priorities: AdminPicklistOption[];
  write_globally_enabled: boolean;
  note_create_supported: boolean;
  suggested_queue_id: number | null;
  suggested_priority_id: number | null;
  column_defaults: Record<string, boolean>;
}

// ---- Admin: per-company form availability ----------------------------------

export interface AdminClientForm {
  form_id: string;
  slug: string;
  title: string;
  category: string | null;
  scope: FormScope;
  is_published: boolean;
  // Effective availability: true when no override row exists, else the row's flag.
  enabled: boolean;
  // Explicit per-company order, or null when the form falls back to title order.
  sort_order: number | null;
  // True when an explicit form_company_config override row exists.
  has_override: boolean;
}

export interface AdminClientFormConfigUpdate {
  enabled?: boolean;
  sort_order?: number;
}

export interface GrantPermissionBody {
  permission: string;
  reason?: string | null;
}

export type AdminRole =
  | "end_user"
  | "approver"
  | "client_admin"
  | "service_desk"
  | "iquest_admin";

export interface AdminUserCreate {
  email: string;
  display_name: string;
  role: AdminRole;
  company_id: string;
  /**
   * Grants to apply with the account. No role implies a permission, so this
   * saves creating the user and then reopening the editor to toggle them.
   * Server-side allow-list; each entry is audit-logged as its own grant.
   */
  permissions?: string[];
}

export interface UserPermissionRow {
  id: string;
  user_id: string;
  permission: string;
  granted_by_user_id: string | null;
  granted_at: string;
  reason: string | null;
}

export interface AdminUsersListParams {
  q?: string;
  company_id?: string;
  staff_only?: boolean;
  /** Include deactivated ('suspended') users. Erased accounts never appear. */
  include_inactive?: boolean;
  page?: number;
  page_size?: number;
}

// ---- Approval engine -------------------------------------------------------

export type ApprovalRequestState =
  | "submitted"
  | "auto_approved"
  | "pending"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled";

export type ApprovalStepType = "sequential" | "parallel_all" | "parallel_any";

export type ApproverDesignationKind = "role" | "tag" | "user" | "direct_manager";

export interface ApproverDesignation {
  designation: ApproverDesignationKind;
  value?: string | null;
}

export interface ApprovalChainStep {
  name: string;
  type: ApprovalStepType;
  approvers: ApproverDesignation[];
}

export interface PendingApprovalRow {
  request_id: string;
  step_id: string;
  step_index: number;
  step_name: string;
  step_type: ApprovalStepType;
  assignment_id: string;
  form_type_slug: string | null;
  requestor_user_id: string | null;
  requestor_display_name: string | null;
  submitted_at: string;
  step_started_at: string | null;
}

export interface HistoryApprovalRow {
  request_id: string;
  step_id: string;
  step_index: number;
  step_name: string;
  assignment_id: string;
  decision: string;
  decision_comment: string | null;
  decided_at: string;
  form_type_slug: string | null;
  requestor_user_id: string | null;
  requestor_display_name: string | null;
  request_state: ApprovalRequestState;
}

export interface RequestListRow {
  id: string;
  state: ApprovalRequestState;
  form_type_slug: string | null;
  current_step_index: number | null;
  submitted_at: string;
  completed_at: string | null;
  rejection_reason: string | null;
}

export interface StepAssignmentDetail {
  id: string;
  original_approver_user_id: string | null;
  original_approver_name: string | null;
  effective_approver_user_id: string | null;
  effective_approver_name: string | null;
  decision: string | null;
  decision_comment: string | null;
  decided_at: string | null;
}

export interface StepDetail {
  id: string;
  step_index: number;
  step_name: string;
  step_type: ApprovalStepType;
  state: string;
  started_at: string | null;
  ended_at: string | null;
  assignments: StepAssignmentDetail[];
}

export interface RequestDetail {
  id: string;
  state: ApprovalRequestState;
  form_type_slug: string | null;
  form_data_snapshot: Record<string, unknown>;
  chain_steps_resolved_jsonb: unknown[];
  current_step_index: number | null;
  submitted_at: string;
  last_transitioned_at: string;
  completed_at: string | null;
  rejection_reason: string | null;
  linked_autotask_ticket_id: number | null;
  linked_autotask_ticket_number: string | null;
  steps: StepDetail[];
}

export interface ApprovalRuleRow {
  id: string;
  priority: number;
  name: string;
  condition_jsonb: Record<string, unknown>;
  action: "bypass" | "chain";
  chain_steps_jsonb: ApprovalChainStep[];
  form_scope_jsonb: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApprovalMatrixResponse {
  id: string;
  company_id: string;
  fallback_approver_user_id: string | null;
  fallback_approver_name: string | null;
  rules: ApprovalRuleRow[];
}

export interface ApprovalRuleCreate {
  priority: number;
  name: string;
  condition_jsonb?: Record<string, unknown>;
  action: "bypass" | "chain";
  chain_steps_jsonb?: ApprovalChainStep[];
  form_scope_jsonb?: string[] | null;
  is_active?: boolean;
}

export interface ApprovalRuleUpdate {
  priority?: number;
  name?: string;
  condition_jsonb?: Record<string, unknown>;
  action?: "bypass" | "chain";
  chain_steps_jsonb?: ApprovalChainStep[];
  form_scope_jsonb?: string[] | null;
  is_active?: boolean;
}

export interface MatrixFallbackPatch {
  fallback_approver_user_id: string | null;
}

export interface MatrixPreviewBody {
  form_type_slug?: string | null;
  form_data?: Record<string, unknown>;
  request_value?: number | null;
  requestor_role?: string | null;
  requestor_tag?: string | null;
}

export interface MatrixPreviewResponse {
  matched_rule_id: string | null;
  matched_rule_name: string | null;
  action: "bypass" | "chain" | null;
  chain_steps_jsonb: ApprovalChainStep[];
  fallback_used: boolean;
}

// ---- Forms ------------------------------------------------------------------

export type FormFieldType =
  | "short_text"
  | "long_text"
  | "number"
  | "date"
  | "datetime"
  | "single_select"
  | "multi_select"
  | "checkbox_group"
  | "single_checkbox"
  | "radio_group"
  | "file_upload"
  | "user_picker"
  | "section_header";

export interface FormFieldOption {
  value: string;
  label: string;
}

/**
 * A named group of fields. Fields reference a section via
 * ``FormFieldSchema.section_id``; sections carry no submitted data.
 */
export interface FormSection {
  id: string;
  title: string;
  description?: string | null;
}

/** Autotask ticket fields a form field can be mapped onto. */
export type AutotaskMappingTarget =
  | "title"
  | "description"
  | "priority"
  | "status"
  | "queueID"
  | "source"
  | "issueType"
  | "subIssueType"
  | "ticketCategory"
  | "contactID"
  | "udf";

export interface AutotaskFieldMapping {
  target: AutotaskMappingTarget;
  /** Required when ``target === "udf"`` — the Autotask UDF name. */
  udf_name?: string | null;
}

export type ConditionalExpression =
  | { type: "and" | "or"; children: ConditionalExpression[] }
  | { type: "not"; children: [ConditionalExpression] }
  | { type: "field_eq"; field_id: string; value: unknown }
  | { type: "field_in"; field_id: string; values: unknown[] }
  | { type: "field_gt" | "field_lt"; field_id: string; value: number }
  | { type: "field_len_gt" | "field_len_lt" | "field_len_eq"; field_id: string; value: number };

export interface FormFieldSchema {
  id: string;
  type: FormFieldType;
  label: string;
  required?: boolean;
  help_text?: string | null;
  placeholder?: string | null;
  options?: FormFieldOption[] | null;
  /**
   * Choice fields: option value(s) pre-selected when the form first loads.
   * A string for single-choice fields (single_select / radio_group); a
   * string[] for multi-choice (multi_select / checkbox_group). null/undefined
   * = no default (the field starts empty).
   */
  default_value?: string | string[] | null;
  min_length?: number | null;
  max_length?: number | null;
  min_value?: number | null;
  max_value?: number | null;
  regex?: string | null;
  decimal_places?: number | null;
  is_currency?: boolean;
  max_files?: number;
  max_file_size_mb?: number;
  accepted_mime_types?: string[] | null;
  user_filter_role?: string | null;
  show_when?: ConditionalExpression | null;
  required_when?: ConditionalExpression | null;
  /** References ``FormSection.id``; null = ungrouped. */
  section_id?: string | null;
  /**
   * Layout width. "full" spans the form row; "half" pairs two-up with the next
   * half-width field on the runner. Presentation-only; defaults to "full".
   */
  width?: "half" | "full";
  /** Maps this field's submitted value onto an Autotask ticket field. */
  autotask_mapping?: AutotaskFieldMapping | null;
}

export type FormIconColor = "violet" | "green" | "orange" | "red" | "blue";

export interface FormInternalNotifyRecipient {
  email: string;
  name?: string | null;
}

export interface FormInternalNotifyConfig {
  /** Master switch: keep the recipient list but pause the fan-out. */
  enabled: boolean;
  recipients: FormInternalNotifyRecipient[];
}

export interface FormSchemaShape {
  title: string;
  description?: string | null;
  submit_label?: string;
  fields: FormFieldSchema[];
  /** Ordered named sections; fields reference these via section_id. */
  sections?: FormSection[];
  /** Accent colour for the form's catalogue/list icon. */
  icon_color?: FormIconColor | null;
  approval_value_field_id?: string | null;
  approval_form_type_slug?: string | null;
  /** Rail metadata (optional): estimated time to complete, e.g. "5-7 min". */
  est_time?: string | null;
  /** Rail metadata (optional): target turnaround SLA, e.g. "2 business days". */
  sla_target?: string | null;
  /** Rail metadata (optional): what submitting this form produces. */
  outputs?: string[];
  /**
   * Note written by the form author, appended to the Autotask ticket
   * description. Not a field the customer fills in, but it is part of the
   * ticket description so the customer can see it on the ticket.
   */
  ticket_description_note?: string | null;
  /**
   * Internal notifications: when a request from this form is fully approved,
   * email each recipient the full submission. iQuest-internal config; the
   * client-facing form response strips this. Sends only when the server-side
   * kill-switch (form_notification_emails_enabled) is on, and only to addresses
   * on an approved email domain.
   */
  internal_notifications?: FormInternalNotifyConfig | null;
}

/** One mappable Autotask target, returned by the autotask-fields endpoint. */
export interface AutotaskFieldTarget {
  target: string;
  label: string;
  kind: "text" | "picklist" | "reference" | "udf";
  /** Populated for picklist targets so the builder can show real values. */
  options?: FormFieldOption[] | null;
}

export interface AutotaskFieldsResponse {
  targets: AutotaskFieldTarget[];
}

export interface FormCatalogueItem {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  /** Estimated time to complete, derived from the published schema. */
  est_time?: string | null;
  /** True when the form routes through the approval engine before fulfilment. */
  has_approval?: boolean;
}

export interface FormPublic {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  category: string | null;
  form_schema: FormSchemaShape;
}

export type FormScope = "global" | "multi_company" | "single_company";

export interface FormAdmin {
  id: string;
  slug: string;
  /** Internal/IT name — what admins sort and search by. */
  title: string;
  /** Customer-facing name; null falls back to ``title``. */
  client_facing_title: string | null;
  description: string | null;
  category: string | null;
  company_id: string | null;
  scope: FormScope;
  audience_company_ids: string[];
  is_published: boolean;
  published_at: string | null;
  published_schema: FormSchemaShape | null;
  draft_schema: FormSchemaShape | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface FormCreateBody {
  title: string;
  client_facing_title?: string | null;
  slug?: string | null;
  description?: string | null;
  category?: string | null;
  company_id?: string | null;
  scope?: FormScope;
  audience_company_ids?: string[] | null;
  draft_schema?: FormSchemaShape | null;
}

export interface FormDraftUpdateBody {
  title?: string | null;
  /** Empty string clears the alias; omit/undefined leaves it untouched. */
  client_facing_title?: string | null;
  description?: string | null;
  category?: string | null;
  draft_schema?: FormSchemaShape | null;
  scope?: FormScope | null;
  company_id?: string | null;
  audience_company_ids?: string[] | null;
}

export interface FormSubmissionResult {
  id: string;
  form_id: string;
  approval_request_id: string | null;
  created_at: string;
  /** Present when a staff direct-push created an Autotask ticket. */
  autotask_ticket_id?: number | null;
  autotask_ticket_number?: string | null;
}

/** One option for a form user-picker field (live Entra via CIPP, or portal). */
export interface PickablePerson {
  value: string;
  label: string;
  sublabel: string;
  source: "entra" | "portal";
}

// ---- Notifications + Delegation --------------------------------------------

export interface NotificationEventRow {
  id: string;
  event: string;
  subject: string | null;
  body: string | null;
  email_sent_at: string | null;
  email_drop_reason: string | null;
  in_portal_read_at: string | null;
  payload_jsonb: Record<string, unknown>;
  created_at: string;
}

export interface NotificationsPage {
  items: NotificationEventRow[];
  total: number;
  page: number;
  page_size: number;
  unread_count: number;
}

export interface NotificationPreferences {
  muted: boolean;
  digest_opt_in: boolean;
  quiet_hours: {
    enabled: boolean;
    start: string;
    end: string;
  };
  // Per-channel delivery toggles. Both default true server-side.
  channels: {
    email: boolean;
    in_portal: boolean;
  };
}

export interface DelegationRow {
  id: string;
  company_id: string;
  original_user_id: string;
  delegate_user_id: string;
  start_date: string;
  end_date: string;
  note: string | null;
  set_by: "self" | "admin";
  set_by_user_id: string | null;
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  created_at: string;
}

export interface DelegateCandidate {
  id: string;
  display_name: string;
  email: string;
  role: string;
}

export interface DelegationSelfCreate {
  delegate_user_id: string;
  start_date: string;
  end_date: string;
  note?: string | null;
}

export interface DelegationAdminCreate extends DelegationSelfCreate {}


// ---- Knowledge Base --------------------------------------------------------

export type KbArticleType = "article" | "guide";

export interface KbStep {
  title: string;
  detail: string;
  tip?: string | null;
}

export interface KbArticleListItem {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  category: string;
  tags: string[];
  scope: "global" | "multi_company" | "single_company";
  published_at: string | null;
  updated_at: string;
  thumbs_up: number;
  thumbs_down: number;
  my_vote: boolean | null;
  article_type: KbArticleType;
  is_saved: boolean;
}

export interface KbArticleDetail extends KbArticleListItem {
  body: string;
  versions_count: number;
  steps: KbStep[] | null;
}

export interface KbAssistSource {
  id: string;
  title: string;
  slug: string;
  summary: string | null;
  article_type: KbArticleType;
}

export interface KbAssistResponse {
  answer: string | null;
  sources: KbAssistSource[];
  kind: "ai" | "extractive" | "no_answer" | "no_results";
}

export interface KbHelpfulnessResponse {
  is_helpful: boolean;
  thumbs_up: number;
  thumbs_down: number;
}

export interface KbAdminListItem {
  id: string;
  title: string;
  slug: string;
  category: string;
  scope: "global" | "multi_company" | "single_company";
  company_id: string | null;
  status: "draft" | "published";
  published_at: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface KbAdminDetail extends KbAdminListItem {
  body: string;
  summary: string | null;
  tags: string[];
  audience_company_ids: string[];
  article_type: KbArticleType;
  steps: KbStep[] | null;
}

export interface KbVersionRow {
  id: string;
  version_number: number;
  title: string;
  category: string;
  scope: string;
  published_by_user_id: string | null;
  published_at: string;
}

export interface KbArticleCreate {
  title: string;
  body: string;
  category: string;
  scope?: string;
  tags?: string[];
  summary?: string | null;
  company_id?: string | null;
  audience_company_ids?: string[] | null;
  article_type?: KbArticleType;
  steps?: KbStep[] | null;
}

export interface KbArticleUpdate {
  title?: string;
  body?: string;
  category?: string;
  scope?: string;
  tags?: string[];
  summary?: string | null;
  company_id?: string | null;
  audience_company_ids?: string[] | null;
  article_type?: KbArticleType;
  steps?: KbStep[] | null;
}

export interface KbAdminPage {
  items: KbAdminListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface KbPublicPage {
  items: KbArticleListItem[];
  total: number;
  page: number;
  page_size: number;
}

// ---- Dashboard --------------------------------------------------------------

export interface DashboardStats {
  my_open_tickets: number | null;
  company_open_tickets: number | null;
  my_pending_requests: number;
  approval_engine_status: "not_yet_built" | "ready";
  sla_met_30d_percent: number | null;
  sla_met_30d_count: number;
  sla_total_30d_count: number;
}

// ---- SLA dashboard ----------------------------------------------------------

/** Where an OPEN ticket stands on its first response. Mirrors POSTURES in
 *  services/sla.py. "responded" and "no_target" were one "no_sla" bucket that
 *  meant both at once (issue #94); "met" was never a posture, it is the rolling
 *  30-day figure reported as sla_met_count. */
export type SlaPosture =
  | "breached"
  | "at_risk"
  | "on_track"
  | "responded"
  | "no_target";

/** Which open tickets the summary's rows cover. The counts always cover all of
 *  them regardless. */
export type SlaShow =
  | "awaiting"
  | "responded"
  | "all"
  /** A different population: every ticket first answered inside the rolling
   *  window, open or closed. This is the compliance percentage's own
   *  denominator, so the list and the figure always reconcile. */
  | "answered_window";

export interface SlaTicketRow {
  id: number | null;
  ticketNumber: string | null;
  title: string | null;
  status_label: string | null;
  priority_label: string | null;
  company_name: string | null;
  posture: SlaPosture;
  /** For an answered ticket: did the response beat the deadline? True in time,
   *  false late, null when the ticket carried no response target. "Responded"
   *  on its own does not tell a customer whether we met the SLA. */
  sla_met: boolean | null;
  first_response_due: string | null;
  resolved_due: string | null;
}

export interface SlaSummary {
  scope: TicketScope;
  show: SlaShow;
  counts: Record<SlaPosture, number>;
  /** breached + at_risk + on_track: what the stat cards add up to, and the row
   *  count when show is "awaiting". */
  awaiting_response: number;
  total_open: number;
  sla_met_percent: number | null;
  sla_met_count: number;
  sla_total_count: number;
  met_window_days: number;
  tickets: SlaTicketRow[];
  /** The row list hit the server-side cap; the counts are still complete. */
  tickets_truncated: boolean;
  /** When the ticket mirror these figures come from was last refreshed. A
   *  breach computed from a stale mirror is stale, not wrong, and the page has
   *  to be able to say so. */
  synced_at: string | null;
}

// ---- Reporting engine (BrightGauge replacement) ----------------------------

export interface ReportingPeriod {
  period: string;
  label: string;
  ticket_count: number;
  is_live: boolean;
  partial: boolean;
}

/** One row of the iQuest-staff client picker on the Reports page (id + name only). */
export interface ReportingCompanyRow {
  id: string;
  name: string;
}

export interface ReportingPeriodsResponse {
  company_id: string;
  periods: ReportingPeriod[];
}

export type ReportingTicketStatus = "Closed" | "Open";
export type CsatBucket = "positive" | "neutral" | "negative";
/** The four ratings iQuest's Customer Thermometer survey offers, stored in the
 *  Autotask UDF as "Gold Star" / "Green Light" / "Yellow Light" / "Red Light" and
 *  shown to customers as Fantastic / Good / Neutral / Review. */
export type CsatTier = "gold" | "green" | "yellow" | "red";

/** One normalized, client-safe ticket row the dashboard aggregates in-browser. */
export interface ReportingTicketRow {
  id: number | null;
  ticketNumber: string | null;
  subject: string;
  contact: string;
  engineer: string;
  priority: string;
  issueType: string;
  subType: string;
  source: string;
  status: ReportingTicketStatus;
  day: number;
  hour: number;
  openedISO: string;
  // The cohort fields below are OPTIONAL on the wire on purpose: the staging
  // bundle runs against the prod backend, which only starts sending them once
  // the day's release publishes. data.ts degrades to the pre-cohort behaviour
  // when they are absent rather than rendering an all-zero dashboard.
  /** Created inside the reporting month: the "tickets opened" cohort, and the
   *  basis of every breakdown, leaderboard and the ticket register. */
  openedInPeriod?: boolean;
  /** Completed inside the reporting month whenever it was opened: the "tickets
   *  completed" cohort. A ticket can be in both cohorts. */
  completedInPeriod?: boolean;
  /** Day-of-month the ticket was completed (AWST), null unless completedInPeriod. */
  closedDay?: number | null;
  /** True for the auto-generated alert queues: counted as volume, never as
   *  response-time / SLA / requester / after-hours evidence. */
  autoGenerated?: boolean;
  /** False when the ticket sits in a queue hidden from the ticket list, so the
   *  detail endpoint 404s it and the drill-down must not render it as a link. */
  detailAvailable?: boolean;
  /** Minutes of BUSINESS time (08:00-17:00 AWST, weekdays, minus WA public
   *  holidays) from open to first response — not wall clock. */
  firstResponseMins: number | null;
  resolutionHours: number | null;
  slaResponseMet: boolean | null;
  afterHours: boolean;
  reopened: boolean;
  /** The raw Customer Thermometer temperature from the Autotask UDF ("Gold Star",
   *  "Green Light", ...). Null when the customer did not rate the ticket. */
  csat: string | null;
  csatBucket: CsatBucket | null;
  /** The rating tier the temperature maps to. Optional on the wire: staging runs
   *  this bundle against a backend that may predate the field. */
  csatTier?: CsatTier | null;
}

export interface ReportingTicketsResponse {
  period: string;
  label: string;
  tickets: ReportingTicketRow[];
  count: number;
  is_live: boolean;
}

// Admin: per-client report recipients + the monthly-review toggle.
export type ReportRecipientGate =
  | "eligible"
  | "monthly_review_disabled"
  | "blocked_suppressed"
  | "blocked_domain";

export interface AdminReportRecipient {
  id: string;
  email: string;
  display_name: string | null;
  source: string;
  portal_user_id: string | null;
  added_at: string;
  gate_status: ReportRecipientGate;
}

export interface AdminReportRecipientsResponse {
  company_id: string;
  monthly_review_enabled: boolean;
  recipients: AdminReportRecipient[];
}

export interface AdminReportRecipientCreate {
  email: string;
  display_name?: string | null;
  portal_user_id?: string | null;
  reason?: string | null;
}

export interface AdminReportingConfig {
  company_id: string;
  monthly_review_enabled: boolean;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export const api = {
  me: () => req<Me>("/api/me"),

  // GDPR self-service: export a copy of the user's personal data, or request
  // account deletion (returns a tombstone state object).
  account: {
    export: () => req<Record<string, unknown>>("/api/me/export"),
    deleteAccount: (reason?: string | null) =>
      req<AccountDeletionTombstone>("/api/me/delete-account", {
        method: "POST",
        body: JSON.stringify({ reason: reason ?? null }),
      }),
  },

  tickets: {
    list: (params: TicketListParams = {}) =>
      req<AutotaskTicketListResponse>(
        `/api/tickets${buildQuery({
          scope: params.scope,
          status: params.status,
          priority: params.priority,
          q: params.q,
          page: params.page,
          page_size: params.page_size,
          sort: params.sort,
          dir: params.dir,
        })}`,
      ),
    get: (ticketNumber: string) =>
      req<AutotaskTicketDetail>(`/api/tickets/${encodeURIComponent(ticketNumber)}`),
    create: (body: { subject: string; description?: string; priority?: string }) =>
      req<{ id: number | null; ticketNumber: string | null }>("/api/tickets", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    addNote: (ticketNumber: string, body: { body: string; title?: string }) =>
      req<{ id: number | null; ticket_id: number | null }>(
        `/api/tickets/${encodeURIComponent(ticketNumber)}/notes`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    changeStatus: (
      ticketNumber: string,
      action: "reopen" | "resolve" | "close",
    ) =>
      req<{
        id: number | null;
        ticket_number: string | null;
        status_id: number | null;
        action: string;
      }>(`/api/tickets/${encodeURIComponent(ticketNumber)}/status`, {
        method: "POST",
        body: JSON.stringify({ action }),
      }),
    uploadAttachment: async (
      ticketNumber: string,
      file: File,
    ): Promise<{ id: number | null; ticket_id: number | null }> => {
      // FormData: let the browser set the multipart boundary (do not route
      // through req(), which forces Content-Type: application/json).
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(
        `${base}/api/tickets/${encodeURIComponent(ticketNumber)}/attachments`,
        {
          method: "POST",
          headers: await authHeaders(),
          body: fd,
        },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      return r.json();
    },
    // Authed binary download: the API is cross-origin + Bearer, so a plain
    // <a href> can't carry the token. Fetch with auth and hand back a Blob plus
    // the server-provided filename (Content-Disposition) for the caller to save.
    downloadAttachment: async (
      ticketNumber: string,
      attachmentId: number,
      fallbackName: string,
    ): Promise<{ blob: Blob; filename: string }> => {
      const r = await fetch(
        `${base}/api/tickets/${encodeURIComponent(ticketNumber)}/attachments/${attachmentId}`,
        { headers: await authHeaders() },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      const blob = await r.blob();
      const disposition = r.headers.get("Content-Disposition") ?? "";
      const filename = parseContentDispositionFilename(disposition) || fallbackName;
      return { blob, filename };
    },
  },

  feedback: {
    config: () => req<{ enabled: boolean }>("/api/feedback/config"),
    list: () => req<SupportTicket[]>("/api/feedback"),
    get: (id: string) => req<SupportTicketDetail>(`/api/feedback/${id}`),
    create: (data: FeedbackCreate) =>
      req<SupportTicket>("/api/feedback", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    reply: (id: string, content: string, attachment_urls?: string[]) =>
      req<TicketComment>(`/api/feedback/${id}/comments`, {
        method: "POST",
        body: JSON.stringify({ content, attachment_urls }),
      }),
    markRead: (id: string) =>
      req<void>(`/api/feedback/${id}/mark-read`, { method: "POST" }),
    unreadCount: () => req<{ count: number }>("/api/feedback/unread-count"),
    uploadAttachment: async (file: File): Promise<FeedbackAttachment> => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${base}/api/feedback/attachments`, {
        method: "POST",
        headers: await authHeaders(),
        body: fd,
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      return r.json();
    },
  },

  announcements: {
    listActive: () => req<AnnouncementPublic[]>("/api/announcements/active"),
    dismiss: (id: string) =>
      req<void>(`/api/announcements/${id}/dismiss`, { method: "POST" }),
    adminList: (page = 1, pageSize = 50) =>
      req<AdminAnnouncementsPage>(
        `/api/admin/announcements?page=${page}&page_size=${pageSize}`,
      ),
    adminGet: (id: string) =>
      req<AnnouncementAdmin>(`/api/admin/announcements/${id}`),
    adminCreate: (data: AnnouncementCreate) =>
      req<AnnouncementAdmin>("/api/admin/announcements", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    adminUpdate: (id: string, patch: AnnouncementUpdate) =>
      req<AnnouncementAdmin>(`/api/admin/announcements/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    adminDelete: (id: string) =>
      req<void>(`/api/admin/announcements/${id}`, { method: "DELETE" }),
    // Status Alerts (Implementation Spec v1.2)
    listClients: () =>
      req<StatusAlertClient[]>("/api/admin/announcements/clients"),
    resolveRecipients: (data: ResolveRecipientsRequest) =>
      req<RecipientFunnel>("/api/admin/announcements/resolve-recipients", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    // Composer preview. Debounced by the caller. Pass a signal to cancel the
    // in-flight render when the author keeps typing. No row is saved.
    renderSummary: (data: RenderSummaryRequest, signal?: AbortSignal) =>
      req<RenderedSummary>("/api/admin/announcements/render-summary", {
        method: "POST",
        body: JSON.stringify(data),
        signal,
      }),
    // Internal only. The server emails the signed-in staff member, never a client.
    testSend: (data: StatusAlertTestSendRequest) =>
      req<StatusAlertTestSendResponse>("/api/admin/announcements/test-send", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    postUpdate: (id: string, data: PostUpdateRequest) =>
      req<AnnouncementAdmin>(`/api/admin/announcements/${id}/updates`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    listUpdates: (id: string) =>
      req<AlertUpdate[]>(`/api/admin/announcements/${id}/updates`),
  },

  dashboard: {
    stats: () => req<DashboardStats>("/api/dashboard/stats"),
  },

  auditLog: {
    // Streams a CSV download. Not JSON, so it bypasses req(): fetch the blob
    // with the bearer token and trigger a browser download.
    exportCsv: async (companyId: string): Promise<void> => {
      const r = await fetch(
        `${base}/api/admin/audit-log/export${buildQuery({ company_id: companyId })}`,
        { headers: await authHeaders() },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${companyId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  },

  sla: {
    summary: (
      params: { scope?: TicketScope; priority?: string; show?: SlaShow } = {},
    ) =>
      req<SlaSummary>(
        `/api/sla/summary${buildQuery({
          scope: params.scope,
          priority: params.priority,
          show: params.show,
        })}`,
      ),
    adminSummary: (companyId: string, priority?: string) =>
      req<SlaSummary>(
        `/api/admin/sla/summary${buildQuery({
          company_id: companyId,
          priority,
        })}`,
      ),
  },

  reporting: {
    // The client picker for iQuest staff (both roles). Staff-only server-side;
    // a client user is pinned to their own company and never loads it.
    companies: () => req<ReportingCompanyRow[]>("/api/reporting/companies"),
    // Non-staff omit company_id (the server pins it to their own company); iQuest
    // staff pass the target company_id.
    periods: (companyId?: string) =>
      req<ReportingPeriodsResponse>(
        `/api/reporting/periods${buildQuery({ company_id: companyId })}`,
      ),
    tickets: (period: string, companyId?: string) =>
      req<ReportingTicketsResponse>(
        `/api/reporting/tickets${buildQuery({ period, company_id: companyId })}`,
      ),
    // Authed PDF download (cross-origin + Bearer, so a plain <a href> can't carry
    // the token): fetch the blob and hand back the server filename, like
    // tickets.downloadAttachment / auditLog.exportCsv.
    downloadReport: async (
      period: string,
      companyId?: string,
      fallbackName = "client-service-review.pdf",
    ): Promise<{ blob: Blob; filename: string }> => {
      const r = await fetch(
        `${base}/api/reporting/report.pdf${buildQuery({ period, company_id: companyId })}`,
        { headers: await authHeaders() },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        throw new Error(`${r.status} ${r.statusText}: ${text}`);
      }
      const blob = await r.blob();
      const disposition = r.headers.get("Content-Disposition") ?? "";
      const filename = parseContentDispositionFilename(disposition) || fallbackName;
      return { blob, filename };
    },
    // The single GLOBAL widget layout (server-persisted). Any report viewer may
    // GET it; only iQuest staff may PUT it (the server enforces this and echoes
    // `editable`). saveDashboard sends the version last loaded for optimistic
    // concurrency — a stale save 409s.
    dashboard: () => req<ReportingDashboardResponse>("/api/reporting/dashboard"),
    saveDashboard: (widgets: unknown[], version: number, reason?: string | null) =>
      req<ReportingDashboardResponse>("/api/reporting/dashboard", {
        method: "PUT",
        body: JSON.stringify({ widgets, version, reason: reason ?? null }),
      }),
  },

  forms: {
    list: () => req<FormCatalogueItem[]>("/api/forms"),
    get: (id: string) => req<FormPublic>(`/api/forms/${id}`),
    submit: (
      id: string,
      form_data: Record<string, unknown>,
      company_id?: string,
    ) =>
      req<FormSubmissionResult>(`/api/forms/${id}/submissions`, {
        method: "POST",
        // company_id is honoured for iQuest staff only (direct push to that
        // client's Autotask); the server ignores it for client users.
        body: JSON.stringify(company_id ? { form_data, company_id } : { form_data }),
      }),
    people: (q?: string, companyId?: string) =>
      req<PickablePerson[]>(
        `/api/forms/people${buildQuery({ q, company_id: companyId })}`,
      ),
  },

  // "View as user" (impersonation) controls for iQuest staff. start/stop are
  // called WITHOUT the impersonation header (services/impersonation.ts manages
  // the timing), so they run as the real staff identity and are audit-logged.
  impersonation: {
    targets: (companyId: string, q?: string) =>
      req<ImpersonationTarget[]>(
        `/api/admin/impersonation/targets${buildQuery({ company_id: companyId, q })}`,
      ),
    start: (userId: string, reason?: string | null) =>
      req<ImpersonationTargetSummary>("/api/admin/impersonation/start", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, reason: reason ?? null }),
      }),
    stop: (userId: string, reason?: string | null) =>
      req<{ ok: boolean }>("/api/admin/impersonation/stop", {
        method: "POST",
        body: JSON.stringify({ user_id: userId, reason: reason ?? null }),
      }),
  },

  admin: {
    companies: {
      list: () => req<AdminCompanyRow[]>("/api/admin/companies"),
    },
    // Autotask company lookup, backing the "Create from Autotask" flow.
    autotask: {
      searchCompanies: (q: string) =>
        req<AutotaskCompanySearchItem[]>(
          `/api/admin/autotask/companies${buildQuery({ q })}`,
        ),
      companyDetails: (autotaskId: number) =>
        req<AutotaskCompanyDetail>(
          `/api/admin/autotask/companies/${autotaskId}/details`,
        ),
      // Pass { background: true } to fire-and-forget the sync (returns
      // immediately); a full sync exceeds the client request timeout.
      sync: (opts: { background?: boolean } = {}) =>
        req<AutotaskSyncResult>(
          `/api/admin/autotask/sync${opts.background ? "?background=true" : ""}`,
          { method: "POST" },
        ),
    },
    clients: {
      // `include: "health"` adds setup_health + tickets_30d, computed server-side
      // in a handful of grouped sweeps. Without it the list stays one query.
      list: (params: { q?: string; is_active?: boolean; include?: "health" } = {}) =>
        req<AdminClientListItem[]>(
          `/api/admin/clients${buildQuery({
            q: params.q,
            is_active: params.is_active === undefined ? undefined : params.is_active ? "true" : "false",
            include: params.include,
          })}`,
        ),
      get: (id: string, params: { include?: "health" } = {}) =>
        req<AdminClientDetail>(
          `/api/admin/clients/${encodeURIComponent(id)}${buildQuery({
            include: params.include,
          })}`,
        ),
      create: (data: AdminClientCreate) =>
        req<AdminClientDetail>("/api/admin/clients", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id: string, data: AdminClientUpdate) =>
        req<AdminClientDetail>(`/api/admin/clients/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      // Permanent removal. deletionImpact is the read-only pre-flight; remove
      // is irreversible and requires the slug echoed back in confirm_slug.
      deletionImpact: (id: string) =>
        req<AdminClientDeletionImpact>(
          `/api/admin/clients/${encodeURIComponent(id)}/deletion-impact`,
        ),
      remove: (id: string, confirmSlug: string, reason?: string | null) =>
        req<AdminClientDeleteResult>(
          `/api/admin/clients/${encodeURIComponent(id)}`,
          {
            method: "DELETE",
            body: JSON.stringify({
              confirm_slug: confirmSlug,
              reason: reason ?? null,
            }),
          },
        ),
      // Per-company Autotask ticket-sync config (own GET/PUT sub-resource).
      ticketSync: {
        // Company-independent picklists + write-gate state, for the wizard.
        options: () =>
          req<AdminTicketSyncOptions>(`/api/admin/ticket-sync-options`),
        get: (id: string) =>
          req<AdminTicketSyncConfig>(
            `/api/admin/clients/${encodeURIComponent(id)}/ticket-sync-config`,
          ),
        update: (id: string, data: AdminTicketSyncConfigUpdate) =>
          req<AdminTicketSyncConfig>(
            `/api/admin/clients/${encodeURIComponent(id)}/ticket-sync-config`,
            { method: "PUT", body: JSON.stringify(data) },
          ),
      },
      // Per-company reporting recipients + the monthly-review toggle (Configure
      // pane "Reporting" tab). Mirrors the ticketSync sub-resource shape.
      reporting: {
        recipients: (id: string) =>
          req<AdminReportRecipientsResponse>(
            `/api/admin/clients/${encodeURIComponent(id)}/report-recipients`,
          ),
        addRecipient: (id: string, data: AdminReportRecipientCreate) =>
          req<AdminReportRecipient>(
            `/api/admin/clients/${encodeURIComponent(id)}/report-recipients`,
            { method: "POST", body: JSON.stringify(data) },
          ),
        removeRecipient: (id: string, email: string) =>
          req<void>(
            `/api/admin/clients/${encodeURIComponent(id)}/report-recipients${buildQuery({ email })}`,
            { method: "DELETE" },
          ),
        setConfig: (
          id: string,
          monthlyReviewEnabled: boolean,
          reason?: string | null,
        ) =>
          req<AdminReportingConfig>(
            `/api/admin/clients/${encodeURIComponent(id)}/reporting-config`,
            {
              method: "PUT",
              body: JSON.stringify({
                monthly_review_enabled: monthlyReviewEnabled,
                reason: reason ?? null,
              }),
            },
          ),
        // Sends the review letter to the SIGNED-IN staff member's own inbox. There
        // is no destination parameter, by design: iQuest admin only, and it can
        // never be aimed at a client. Customers only ever receive this on the 7th;
        // this is how we check it on any other day. `period` (YYYY-MM) defaults to
        // last month server-side.
        sendTestEmail: (id: string, period?: string | null) =>
          req<{ sent_to: string; period: string }>(
            `/api/admin/clients/${encodeURIComponent(id)}/reporting-test-email`,
            {
              method: "POST",
              body: JSON.stringify({ period: period ?? null }),
            },
          ),
      },
      // Re-sweep this client's licensed Entra mailboxes from CIPP now. The
      // mirror refreshes daily at 05:00 AWST, which is right for the steady
      // state and wrong for one moment: a client onboarded (or an M365 tenant
      // id entered) after this morning's sweep has no Entra audience at all
      // until tomorrow. Scoped to one client on purpose — an all-tenant sweep
      // is minutes of Graph calls and belongs to the scheduler.
      cippMailboxes: {
        refresh: (id: string) =>
          req<CippMailboxRefreshResult>("/api/admin/cipp/mailboxes/refresh", {
            method: "POST",
            body: JSON.stringify({ company_id: id }),
          }),
      },
      // Synced Autotask contacts for this client, as portal-user import
      // candidates (preview), plus the import action itself.
      autotaskContacts: {
        list: (id: string) =>
          req<AutotaskContactImportPreview>(
            `/api/admin/clients/${encodeURIComponent(id)}/autotask-contacts`,
          ),
        import: (id: string, autotaskContactIds: number[]) =>
          req<AutotaskContactImportResult>(
            `/api/admin/clients/${encodeURIComponent(id)}/autotask-contacts/import`,
            {
              method: "POST",
              body: JSON.stringify({ autotask_contact_ids: autotaskContactIds }),
            },
          ),
      },
      // Effective per-company form availability + ordering.
      listForms: (id: string) =>
        req<AdminClientForm[]>(
          `/api/admin/clients/${encodeURIComponent(id)}/forms`,
        ),
      setFormConfig: (id: string, formId: string, data: AdminClientFormConfigUpdate) =>
        req<AdminClientForm>(
          `/api/admin/clients/${encodeURIComponent(id)}/forms/${encodeURIComponent(formId)}`,
          { method: "PATCH", body: JSON.stringify(data) },
        ),
    },
    forms: {
      list: (params: { company_id?: string; include_unpublished?: boolean } = {}) =>
        req<FormAdmin[]>(
          `/api/admin/forms${buildQuery({
            company_id: params.company_id,
            include_unpublished:
              params.include_unpublished === undefined
                ? undefined
                : params.include_unpublished
                  ? "true"
                  : "false",
          })}`,
        ),
      get: (id: string) => req<FormAdmin>(`/api/admin/forms/${id}`),
      // Companies for the catalogue scope-name display + per-customer filter.
      // Forms-admin gated, so service_desk can read it (unlike admin.companies).
      companies: () => req<AdminCompanyRow[]>("/api/admin/forms/companies"),
      autotaskFields: () =>
        req<AutotaskFieldsResponse>("/api/admin/forms/autotask-fields"),
      create: (data: FormCreateBody) =>
        req<FormAdmin>("/api/admin/forms", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      updateDraft: (id: string, patch: FormDraftUpdateBody) =>
        req<FormAdmin>(`/api/admin/forms/${id}/draft`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      publish: (id: string) =>
        req<FormAdmin>(`/api/admin/forms/${id}/publish`, { method: "POST" }),
      duplicate: (id: string) =>
        req<FormAdmin>(`/api/admin/forms/${id}/duplicate`, { method: "POST" }),
      remove: (id: string) =>
        req<void>(`/api/admin/forms/${id}`, { method: "DELETE" }),
    },
    users: {
      list: (params: AdminUsersListParams = {}) =>
        req<AdminUsersPage>(
          `/api/admin/users${buildQuery({
            q: params.q,
            company_id: params.company_id,
            staff_only: params.staff_only ? "true" : undefined,
            include_inactive: params.include_inactive ? "true" : undefined,
            page: params.page,
            page_size: params.page_size,
          })}`,
        ),
      create: (data: AdminUserCreate) =>
        req<AdminUserRow>("/api/admin/users", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      // Reversible access cut. 'suspended' blocks sign-in and drops the user
      // from every picker and email roster; 'active' restores them as they were.
      setStatus: (
        userId: string,
        newStatus: "active" | "suspended",
        reason?: string | null,
      ) =>
        req<AdminUserRow>(
          `/api/admin/users/${encodeURIComponent(userId)}/status`,
          {
            method: "PATCH",
            body: JSON.stringify({ status: newStatus, reason: reason ?? null }),
          },
        ),
      // Change a user's role. Audit-logged with before/after. The staff roles
      // (service_desk, iquest_admin) can only be assigned by an iQuest admin,
      // and the last active iQuest admin cannot be demoted — both enforced
      // server-side, so surface the error rather than pre-empting it.
      setRole: (userId: string, newRole: AdminRole, reason?: string | null) =>
        req<AdminUserRow>(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
          method: "PATCH",
          body: JSON.stringify({ role: newRole, reason: reason ?? null }),
        }),
      // Name is always correctable. Work email only while email_editable
      // (never signed in, no SSO subject). The server audits before/after
      // and refuses a locked email with 409.
      updateProfile: (
        userId: string,
        patch: { display_name?: string; email?: string; reason?: string | null },
      ) =>
        req<AdminUserRow>(`/api/admin/users/${encodeURIComponent(userId)}/profile`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      // Irreversible: soft-delete + anonymize (right to erasure). iQuest admins only.
      remove: (userId: string, reason?: string | null) =>
        req<void>(
          `/api/admin/users/${encodeURIComponent(userId)}${buildQuery({
            reason: reason || undefined,
          })}`,
          { method: "DELETE" },
        ),
      grant: (userId: string, permission: string, reason?: string | null) =>
        req<UserPermissionRow>(
          `/api/admin/users/${encodeURIComponent(userId)}/permissions`,
          {
            method: "POST",
            body: JSON.stringify({
              permission,
              reason: reason ?? null,
            } satisfies GrantPermissionBody),
          },
        ),
      revoke: (userId: string, permission: string) =>
        req<void>(
          `/api/admin/users/${encodeURIComponent(userId)}/permissions/${encodeURIComponent(
            permission,
          )}`,
          { method: "DELETE" },
        ),
    },
    matrix: {
      get: (companyId: string) =>
        req<ApprovalMatrixResponse>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}`,
        ),
      setFallback: (companyId: string, patch: MatrixFallbackPatch) =>
        req<ApprovalMatrixResponse>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}`,
          { method: "PATCH", body: JSON.stringify(patch) },
        ),
      createRule: (companyId: string, body: ApprovalRuleCreate) =>
        req<ApprovalRuleRow>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}/rules`,
          { method: "POST", body: JSON.stringify(body) },
        ),
      updateRule: (companyId: string, ruleId: string, body: ApprovalRuleUpdate) =>
        req<ApprovalRuleRow>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}/rules/${encodeURIComponent(ruleId)}`,
          { method: "PATCH", body: JSON.stringify(body) },
        ),
      deleteRule: (companyId: string, ruleId: string) =>
        req<void>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}/rules/${encodeURIComponent(ruleId)}`,
          { method: "DELETE" },
        ),
      preview: (companyId: string, body: MatrixPreviewBody) =>
        req<MatrixPreviewResponse>(
          `/api/admin/matrix/${encodeURIComponent(companyId)}/preview`,
          { method: "POST", body: JSON.stringify(body) },
        ),
    },
  },

  approvals: {
    listPending: () => req<PendingApprovalRow[]>("/api/approvals/pending"),
    listHistory: (page = 1, pageSize = 50) =>
      req<HistoryApprovalRow[]>(
        `/api/approvals/history?page=${page}&page_size=${pageSize}`,
      ),
    approve: (requestId: string, comment?: string | null) =>
      req<{ request_id: string; state: string; warnings?: string[] }>(
        `/api/approvals/${encodeURIComponent(requestId)}/approve`,
        {
          method: "POST",
          body: JSON.stringify({ comment: comment ?? null }),
        },
      ),
    reject: (requestId: string, comment?: string | null) =>
      req<{ request_id: string; state: string }>(
        `/api/approvals/${encodeURIComponent(requestId)}/reject`,
        {
          method: "POST",
          body: JSON.stringify({ comment: comment ?? null }),
        },
      ),
  },

  requests: {
    list: (state?: ApprovalRequestState, page = 1, pageSize = 50) =>
      req<RequestListRow[]>(
        `/api/requests${buildQuery({
          state: state ?? undefined,
          page,
          page_size: pageSize,
        })}`,
      ),
    get: (requestId: string) =>
      req<RequestDetail>(`/api/requests/${encodeURIComponent(requestId)}`),
    cancel: (requestId: string) =>
      req<{ request_id: string; state: string }>(
        `/api/requests/${encodeURIComponent(requestId)}/cancel`,
        { method: "POST" },
      ),
  },

  notifications: {
    list: (params: { unread_only?: boolean; page?: number; page_size?: number } = {}) =>
      req<NotificationsPage>(
        `/api/notifications${buildQuery({
          unread_only: params.unread_only ? "true" : undefined,
          page: params.page,
          page_size: params.page_size,
        })}`,
      ),
    unreadCount: () => req<{ count: number }>("/api/notifications/unread-count"),
    markRead: (id: string) =>
      req<void>(`/api/notifications/${encodeURIComponent(id)}/mark-read`, {
        method: "POST",
      }),
    markAllRead: () =>
      req<void>(`/api/notifications/mark-all-read`, { method: "POST" }),
    getPreferences: () =>
      req<NotificationPreferences>("/api/notifications/preferences"),
    updatePreferences: (prefs: NotificationPreferences) =>
      req<NotificationPreferences>("/api/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify(prefs),
      }),
  },

  delegations: {
    listMine: () => req<DelegationRow[]>("/api/me/delegations"),
    listMyCandidates: () =>
      req<DelegateCandidate[]>("/api/me/delegations/candidates"),
    createMine: (body: DelegationSelfCreate) =>
      req<DelegationRow>("/api/me/delegations", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    revokeMine: (id: string) =>
      req<void>(`/api/me/delegations/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    listForUser: (userId: string) =>
      req<DelegationRow[]>(
        `/api/admin/users/${encodeURIComponent(userId)}/delegations`,
      ),
    listCandidatesForUser: (userId: string) =>
      req<DelegateCandidate[]>(
        `/api/admin/users/${encodeURIComponent(userId)}/delegations/candidates`,
      ),
    createForUser: (userId: string, body: DelegationAdminCreate) =>
      req<DelegationRow>(
        `/api/admin/users/${encodeURIComponent(userId)}/delegations`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
    revokeForUser: (userId: string, id: string) =>
      req<void>(
        `/api/admin/users/${encodeURIComponent(userId)}/delegations/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  kb: {
    list: (params: { q?: string; category?: string; page?: number; page_size?: number } = {}) =>
      req<KbPublicPage>(`/api/kb${buildQuery({ q: params.q, category: params.category, page: params.page, page_size: params.page_size })}`),
    categories: () => req<string[]>("/api/kb/categories"),
    get: (id: string) => req<KbArticleDetail>(`/api/kb/${encodeURIComponent(id)}`),
    related: (id: string) =>
      req<KbArticleListItem[]>(`/api/kb/${encodeURIComponent(id)}/related`),
    vote: (id: string, is_helpful: boolean, comment?: string | null) =>
      req<KbHelpfulnessResponse>(`/api/kb/${encodeURIComponent(id)}/helpfulness`, {
        method: "POST",
        body: JSON.stringify({ is_helpful, comment: comment ?? null }),
      }),
    guides: () => req<KbArticleListItem[]>("/api/kb/guides"),
    saved: () => req<KbArticleListItem[]>("/api/kb/saved"),
    recent: () => req<KbArticleListItem[]>("/api/kb/recent"),
    assist: (query: string) =>
      req<KbAssistResponse>("/api/kb/assist", {
        method: "POST",
        body: JSON.stringify({ query }),
      }),
    save: (id: string) =>
      req<void>(`/api/kb/${encodeURIComponent(id)}/save`, { method: "POST" }),
    unsave: (id: string) =>
      req<void>(`/api/kb/${encodeURIComponent(id)}/save`, { method: "DELETE" }),
    recordView: (id: string) =>
      req<void>(`/api/kb/${encodeURIComponent(id)}/view`, { method: "POST" }),
  },

  adminKb: {
    list: (params: { q?: string; status?: string; category?: string; page?: number; page_size?: number } = {}) =>
      req<KbAdminPage>(`/api/admin/kb${buildQuery({ q: params.q, status: params.status, category: params.category, page: params.page, page_size: params.page_size })}`),
    get: (id: string) => req<KbAdminDetail>(`/api/admin/kb/${encodeURIComponent(id)}`),
    create: (data: KbArticleCreate) =>
      req<KbAdminDetail>("/api/admin/kb", { method: "POST", body: JSON.stringify(data) }),
    update: (id: string, patch: KbArticleUpdate) =>
      req<KbAdminDetail>(`/api/admin/kb/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) }),
    publish: (id: string) =>
      req<KbAdminDetail>(`/api/admin/kb/${encodeURIComponent(id)}/publish`, { method: "POST" }),
    versions: (id: string) =>
      req<KbVersionRow[]>(`/api/admin/kb/${encodeURIComponent(id)}/versions`),
    rollback: (id: string, versionId: string) =>
      req<KbAdminDetail>(`/api/admin/kb/${encodeURIComponent(id)}/rollback/${encodeURIComponent(versionId)}`, { method: "POST" }),
    remove: (id: string) =>
      req<void>(`/api/admin/kb/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },
};
